import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Automation } from '~api';
import { api } from '~portal/api';
import { AutomationsPageHeader } from '~portal/components/admin/automations/AutomationsPageShell';
import { formatStamp } from '~portal/components/admin/automations/runs/run-format';
import { StatusBadge } from '~portal/components/admin/automations/WorkflowDetailView';
import { AnchorLink } from '~portal/components/common';
import * as commonStyles from '~portal/components/common/common.css';
import { useAdminAccess } from '~portal/hooks';
import { logger } from '~portal/lib';
import { errorMessage } from '~portal/lib/automations';
import {
  matchesEnabled,
  parseAutomationsSearch,
  withAutomationsSearch,
  workflowListInput,
  type WorkflowStatusFilter,
} from '~portal/lib/automations-search';

/**
 * @desc The Workflows list (H2-7, task 8.5). Filters (status, enabled) live in the URL
 * (`parseAutomationsSearch`) and change in place (`resetScroll: false`). The list refreshes on
 * the admin room's `workflow` hint, so a workflow the authoring agent creates for another admin
 * appears here without a reload (the D21 walkthrough).
 */
export const Route = createFileRoute('/_protected/admin/ario/workflows')({
  validateSearch: parseAutomationsSearch,
  component: WorkflowsPage,
});

/** @desc Wide enough for the longest chip, "Disabled" or "Archived", at badge size `sm`. */
const BADGE_COLUMN = 110;

function CreateWorkflowModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const utils = api.useUtils();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = api.automations.workflow.create.useMutation({
    onSuccess: async (workflow) => {
      await utils.automations.workflow.list.invalidate();
      onClose();
      setName('');
      setDescription('');
      navigate({
        to: '/admin/ario/workflows/$workflowId',
        params: { workflowId: workflow._id },
      }).catch((err: unknown) => {
        logger.error('[Workflows] Navigation failed', { error: errorMessage(err) });
      });
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="New workflow" centered>
      <Stack gap="sm">
        <TextInput
          label="Name"
          required
          maxLength={Automation.MAX_NAME_LENGTH}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          data-autofocus
        />
        <Textarea
          label="Description"
          autosize
          minRows={2}
          maxLength={Automation.MAX_DESCRIPTION_LENGTH}
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
        />
        {create.error && (
          <Alert color="red" variant="light" p="xs">
            {create.error.message}
          </Alert>
        )}
        <Text size="xs" c="dimmed">
          A new workflow starts empty and disabled. Add steps, accept a version, then schedule it.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() =>
              create.mutate({ name: name.trim(), description: description.trim(), tags: [] })
            }
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function WorkflowsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [creating, setCreating] = useState(false);
  const list = api.automations.workflow.list.useQuery(workflowListInput(search));
  const rows = (list.data ?? []).filter((workflow) => matchesEnabled(workflow, search));
  const { ario } = useAdminAccess();

  const update = (patch: Parameters<typeof withAutomationsSearch>[1]) => {
    navigate({ search: (prev) => withAutomationsSearch(prev, patch), resetScroll: false }).catch(
      (err: unknown) => {
        logger.error('[Workflows] Navigation failed', { error: errorMessage(err) });
      },
    );
  };

  return (
    <Stack gap="md" p="md" data-testid="workflows-page">
      <AutomationsPageHeader
        title="Workflows"
        description="Steps that turn collected files into outputs, run on a schedule or on demand."
        actions={
          <Button
            leftSection={<IconPlus size={14} aria-hidden />}
            onClick={() => setCreating(true)}
            disabled={!ario.write}
            data-testid="workflows-new"
          >
            New workflow
          </Button>
        }
      />
      <Group gap="sm" wrap="wrap">
        <SegmentedControl
          size="xs"
          value={search.status ?? 'all'}
          onChange={(value) =>
            update({ status: value === 'all' ? null : (value as WorkflowStatusFilter) })
          }
          data={[
            { value: 'all', label: 'All' },
            { value: 'draft', label: 'Draft' },
            { value: 'ready', label: 'Ready' },
            { value: 'archived', label: 'Archived' },
          ]}
        />
        <Select
          size="xs"
          w={160}
          aria-label="Enabled"
          allowDeselect={false}
          value={search.enabled === undefined ? 'any' : String(search.enabled)}
          onChange={(value) =>
            update({ enabled: value === 'any' || value === null ? null : value === 'true' })
          }
          data={[
            { value: 'any', label: 'Enabled or not' },
            { value: 'true', label: 'Enabled' },
            { value: 'false', label: 'Disabled' },
          ]}
        />
      </Group>

      {list.error && (
        <Alert color="red" variant="light" title="Workflows could not be loaded">
          {list.error.message}
        </Alert>
      )}
      {list.isLoading ? (
        <Skeleton height={160} />
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          No workflows match. Ask for one in Chat, or create one here.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover verticalSpacing="xs" data-testid="workflows-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                {/* Fixed widths for the chip and date columns: a badge's label clips with an
                    ellipsis, so an auto-sized column let a long description squeeze "READY" and
                    "ENABLED" to "REA…" at 1600 px. The name column takes the rest. */}
                <Table.Th w={BADGE_COLUMN}>Status</Table.Th>
                <Table.Th w={BADGE_COLUMN}>Enabled</Table.Th>
                <Table.Th w={160}>Versions</Table.Th>
                <Table.Th w={150}>Updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((workflow) => (
                <Table.Tr key={workflow._id} data-testid={`workflow-row-${workflow._id}`}>
                  <Table.Td>
                    <AnchorLink
                      to="/admin/ario/workflows/$workflowId"
                      params={{ workflowId: workflow._id }}
                      fw={500}
                      size="sm"
                    >
                      {workflow.name}
                    </AnchorLink>
                    {workflow.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {workflow.description}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={workflow.status} />
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="outline" color={workflow.enabled ? 'green' : 'gray'}>
                      {workflow.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {workflow.currentVersionId ? 'Current' : 'None accepted'}
                      {workflow.draftVersionId ? ' · draft pending' : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" className={commonStyles.nowrap}>
                      {formatStamp(workflow.updatedAt)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <CreateWorkflowModal opened={creating} onClose={() => setCreating(false)} />
    </Stack>
  );
}
